import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Card, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ReportedUserDetails = ({ user, onResolve }) => {
  const [fullDetails, setFullDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [resolveModal, setResolveModal] = useState(false);
  const [resolveAction, setResolveAction] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [disableModal, setDisableModal] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  useEffect(() => {
    const fetchFullDetails = async () => {
      try {
        // Extract userId - check multiple possible locations
        const userId = user?.userId || user?.user?.id || user?.id;
        
        console.log('Fetching reported user details for userId:', userId);
        console.log('User object:', user);
        
        if (!userId) {
          console.error('No userId found in user object:', user);
          toast.error('Invalid user data: userId not found');
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/admin/reported-users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        
        console.log('Reported user details response:', response.data);
        setFullDetails(response.data);
      } catch (err) {
        console.error('Failed to fetch user details:', err);
        console.error('Error response:', err.response?.data);
        toast.error(err.response?.data?.error || 'Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchFullDetails();
    } else {
      setLoading(false);
    }
  }, [user, API_BASE_URL]);

  const handleResolve = async (action) => {
    setResolving(true);
    try {
      const userId = user?.userId || user?.user?.id || user?.id;
      const response = await axios.post(
        `${API_BASE_URL}/admin/reported-users/${userId}/resolve`,
        { action },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(response.data.message || 'Action completed successfully');
      setResolveModal(false);
      setResolveAction(null);
      
      // Refresh the full details to get updated status
      const detailsResponse = await axios.get(
        `${API_BASE_URL}/admin/reported-users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setFullDetails(detailsResponse.data);
      
      // Call the onResolve callback to refresh the list
      if (onResolve) {
        setTimeout(() => {
          onResolve();
        }, 500);
      }
    } catch (err) {
      console.error('Error resolving user:', err);
      toast.error(err?.response?.data?.error || 'Failed to perform action');
    } finally {
      setResolving(false);
    }
  };

  const handleDisableUser = async () => {
    setDisabling(true);
    try {
      const userId = user?.userId || user?.user?.id || user?.id;
      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${userId}/disable`,
        { reason: disableReason || 'Account disabled by administrator due to reported violations' },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(response.data.message || 'User account has been disabled successfully');
      setDisableModal(false);
      setDisableReason('');
      
      // Refresh the full details to get updated status
      const detailsResponse = await axios.get(
        `${API_BASE_URL}/admin/reported-users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setFullDetails(detailsResponse.data);
      
      // Call the onResolve callback to refresh the list
      if (onResolve) {
        setTimeout(() => {
          onResolve();
        }, 500);
      }
    } catch (err) {
      console.error('Error disabling user:', err);
      toast.error(err?.response?.data?.error || 'Failed to disable user account');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return <p>Loading details...</p>;
  }

  if (!fullDetails) {
    return <p style={{ color: 'red' }}>Failed to load user details</p>;
  }

  // Backend returns 'reportedUser' not 'user'
  const userData = fullDetails.reportedUser || fullDetails.user;
  const reports = fullDetails.reports || [];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Reported User Information */}
      <Card className="mb-3 p-3">
        <h5 className="mb-3">Reported User Information</h5>
        <Row>
          <Col md={4}>
            {userData?.selfieUrl && (
              <img
                src={userData.selfieUrl}
                alt={userData.fullName}
                style={{
                  width: '100%',
                  maxWidth: '200px',
                  borderRadius: '10px',
                  marginBottom: '15px',
                }}
              />
            )}
          </Col>
          <Col md={8}>
            <p><strong>Name:</strong> {userData?.fullName || 'N/A'}</p>
            <p><strong>Email:</strong> {userData?.email || 'N/A'}</p>
            <p><strong>Phone:</strong> {userData?.phoneNumber || 'N/A'}</p>
            <p><strong>University:</strong> {userData?.university || 'N/A'}</p>
            <p><strong>Verified:</strong> {userData?.isVerified ? 'Yes' : 'No'}</p>
            <p><strong>Status:</strong> 
              <span style={{ 
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: userData?.userStatus === 'Removed' || userData?.userStatus === 'Deleted' 
                  ? '#dc3545' 
                  : userData?.userStatus === 'Pending' 
                  ? '#ffc107' 
                  : '#28a745',
                color: 'white',
                fontSize: '12px'
              }}>
                {userData?.userStatus || 'Active'}
              </span>
            </p>
            <p><strong>Member Since:</strong> {new Date(userData?.createdAt).toLocaleDateString()}</p>
          </Col>
        </Row>
      </Card>

      {/* Reports */}
      <Card className="mb-3 p-3">
        <h5 className="mb-3">Reports ({reports.length})</h5>
        {reports.length > 0 ? (
          <div>
            {reports.map((report, idx) => (
              <Card key={report.id} className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa' }}>
                <Row>
                  <Col md={6}>
                    <p><strong>Report #{idx + 1}</strong></p>
                    <p><strong>Reason:</strong> {report.reasonCode}</p>
                    {report.reasonText && (
                      <p><strong>Details:</strong> {report.reasonText}</p>
                    )}
                    <p><strong>Reported At:</strong> {new Date(report.reportedAt).toLocaleString()}</p>
                    <p><strong>Status:</strong> 
                      <span style={{ 
                        marginLeft: '8px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: report.status === 'PENDING' ? '#ffc107' : 
                                       report.status === 'DISMISSED' ? '#6c757d' : '#28a745',
                        color: 'white',
                        fontSize: '11px'
                      }}>
                        {report.status}
                      </span>
                    </p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Reported By:</strong></p>
                    <p style={{ fontWeight: 'bold' }}>{report.reporter?.fullName || 'N/A'}</p>
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      <strong>Email:</strong> {report.reporter?.email || 'N/A'}
                    </p>
                    {report.reporter?.phoneNumber && (
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        <strong>Phone:</strong> {report.reporter.phoneNumber}
                      </p>
                    )}
                    {report.reporter?.university && (
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        <strong>University:</strong> {report.reporter.university}
                      </p>
                    )}
                  </Col>
                </Row>

                {/* Product Information */}
                {report.product && (
                  <div className="mt-3 pt-3 border-top">
                    <p><strong>Related Product:</strong></p>
                    <Row>
                      <Col md={3}>
                        {report.product.images && Array.isArray(report.product.images) && report.product.images.length > 0 && (
                          <div>
                            {report.product.images[0]?.fileUrl && (
                              <img
                                src={report.product.images[0].fileUrl}
                                alt={report.product.title}
                                style={{
                                  width: '100%',
                                  maxWidth: '150px',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  marginBottom: '10px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(report.product.images[0].fileUrl, '_blank')}
                              />
                            )}
                            {report.product.images.length > 1 && (
                              <p style={{ fontSize: '11px', color: '#666' }}>
                                +{report.product.images.length - 1} more image{report.product.images.length - 1 > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        )}
                      </Col>
                      <Col md={9}>
                        <p><strong>Title:</strong> {report.product.title}</p>
                        {report.product.name && report.product.name !== report.product.title && (
                          <p><strong>Name:</strong> {report.product.name}</p>
                        )}
                        <p><strong>Category:</strong> {report.product.category}</p>
                        {report.product.condition && (
                          <p><strong>Condition:</strong> {report.product.condition}</p>
                        )}
                        <p><strong>Price:</strong> ${report.product.price || 'N/A'}</p>
                        <p><strong>Status:</strong> 
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: report.product.status === 'For Sale' ? '#28a745' : 
                                           report.product.status === 'Sold' ? '#007bff' : 
                                           report.product.status === 'Removed' ? '#dc3545' : '#6c757d',
                            color: 'white',
                            fontSize: '11px'
                          }}>
                            {report.product.status}
                          </span>
                        </p>
                        {report.product.description && (
                          <p><strong>Description:</strong> 
                            <span style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '5px' }}>
                              {report.product.description.length > 150 
                                ? `${report.product.description.substring(0, 150)}...` 
                                : report.product.description}
                            </span>
                          </p>
                        )}
                        {report.product.seller && (
                          <p><strong>Seller:</strong> {report.product.seller.fullName || 'N/A'} ({report.product.seller.email || 'N/A'})</p>
                        )}
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Chat Messages */}
                {(report.chatMessages && report.chatMessages.length > 0) || (report.messages && report.messages.length > 0) ? (
                  <div className="mt-3 pt-3 border-top">
                    <p><strong>Chat Messages ({(report.chatMessages || report.messages || []).length}):</strong></p>
                    {report.conversationId && (
                      <p style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>
                        Conversation ID: {report.conversationId}
                      </p>
                    )}
                    <div style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      marginTop: '10px'
                    }}>
                      {(report.chatMessages || report.messages || []).map((msg, msgIdx) => {
                        const isFromReporter = msg.senderId === report.reporter?.id;
                        return (
                          <div
                            key={msg.messageId || msgIdx}
                            style={{
                              marginBottom: '15px',
                              padding: '10px',
                              backgroundColor: isFromReporter ? '#e3f2fd' : '#f5f5f5',
                              borderRadius: '8px',
                              marginLeft: isFromReporter ? '20%' : '0',
                              marginRight: isFromReporter ? '0' : '20%',
                            }}
                          >
                            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                              {isFromReporter ? report.reporter?.fullName : userData?.fullName}
                            </div>
                            {msg.messageType === 'image' && (msg.attachmentData?.url || msg.files) ? (
                              <div>
                                <img
                                  src={msg.attachmentData?.url || (msg.files && Array.isArray(msg.files) && msg.files[0]?.url) || (msg.files && msg.files.url)}
                                  alt="Chat attachment"
                                  style={{
                                    maxWidth: '200px',
                                    maxHeight: '200px',
                                    borderRadius: '4px',
                                    marginBottom: '5px',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => window.open(msg.attachmentData?.url || (msg.files && Array.isArray(msg.files) && msg.files[0]?.url) || (msg.files && msg.files.url), '_blank')}
                                />
                                {msg.message && <p style={{ margin: 0, marginTop: '5px' }}>{msg.message}</p>}
                              </div>
                            ) : msg.priceData ? (
                              <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <p style={{ margin: 0, fontWeight: 'bold' }}>Price Offer: ${msg.priceData.price || 'N/A'}</p>
                                {msg.message && <p style={{ margin: '5px 0 0 0' }}>{msg.message}</p>}
                              </div>
                            ) : msg.productData ? (
                              <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                                <p style={{ margin: 0, fontWeight: 'bold' }}>Product Shared</p>
                                {msg.message && <p style={{ margin: '5px 0 0 0' }}>{msg.message}</p>}
                              </div>
                            ) : (
                              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.message || '(No message text)'}</p>
                            )}
                            <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                              {new Date(msg.timestamp || msg.createdAt).toLocaleString()}
                              {msg.isRead !== undefined && (
                                <span style={{ marginLeft: '10px' }}>
                                  {msg.isRead ? '✓ Read' : '○ Unread'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : report.conversationId ? (
                  <div className="mt-3 pt-3 border-top">
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                      Chat conversation ID: {report.conversationId}
                    </p>
                    <p style={{ color: '#666', fontSize: '12px' }}>
                      Note: Chat messages could not be loaded. Conversation may have been deleted or is not accessible.
                    </p>
                  </div>
                ) : null}

                {!report.product && !report.conversationId && (
                  <div className="mt-3 pt-3 border-top">
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                      No chat or product information available for this report.
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <p>No reports found</p>
        )}
      </Card>

      {/* Action Buttons */}
      <Card className="mt-4 p-3">
        <h5 className="mb-3">Actions</h5>
        {(() => {
          const reportStatuses = reports.map(r => r.status?.toUpperCase());
          const hasPending = reportStatuses.some(s => s === 'PENDING');
          const allDismissed = reportStatuses.length > 0 && reportStatuses.every(s => s === 'DISMISSED');
          const allResolved = reportStatuses.length > 0 && reportStatuses.every(s => s === 'RESOLVED');
          const isUserDisabled = userData?.userStatus === 'Removed' || userData?.userStatus === 'Deleted';
          
          return (
            <div>
              {/* Report Resolution Buttons - Only show if there are pending reports */}
              {hasPending && !allDismissed && !allResolved && (
                <div className="mb-3">
                  <h6>Report Actions:</h6>
                  <div className="text-start">
                    <Button 
                      color="warning" 
                      className="me-2"
                      onClick={() => {
                        setResolveAction('dismiss');
                        setResolveModal(true);
                      }}
                    >
                      Dismiss Reports
                    </Button>
                    <Button 
                      color="success"
                      onClick={() => {
                        setResolveAction('resolve');
                        setResolveModal(true);
                      }}
                    >
                      Resolve Reports
                    </Button>
                  </div>
                </div>
              )}
              
              {/* User Account Actions */}
              <div>
                <h6>Account Actions:</h6>
                {isUserDisabled ? (
                  <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                    This user account has been disabled. The user cannot sign in.
                  </p>
                ) : (
                  <div className="text-start">
                    <Button 
                      color="danger"
                      onClick={() => setDisableModal(true)}
                    >
                      Disable User Account
                    </Button>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                      This will prevent the user from signing in. The account will be marked as "Removed".
                    </p>
                  </div>
                )}
              </div>
              
              {/* Status Messages */}
              {!hasPending && (allDismissed || allResolved) && (
                <div className="mt-3">
                  <p style={{ color: '#6c757d', fontStyle: 'italic', marginBottom: '10px' }}>
                    {allDismissed
                      ? 'All reports have been dismissed.'
                      : allResolved
                      ? 'All reports have been resolved.'
                      : ''}
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </Card>

      {/* Resolve Confirmation Modal */}
      <Modal isOpen={resolveModal} toggle={() => setResolveModal(false)} size="lg">
        <ModalHeader toggle={() => setResolveModal(false)}>
          {resolveAction === 'dismiss' ? 'Dismiss Reports' : 'Resolve Reports'}
        </ModalHeader>
        <ModalBody>
          {resolveAction === 'dismiss' ? (
            <p>Are you sure you want to dismiss all reports for this user? The reports will be marked as dismissed and no further action will be taken.</p>
          ) : (
            <p>Are you sure you want to resolve all reports for this user? The reports will be marked as resolved, indicating that appropriate action has been taken.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color={resolveAction === 'dismiss' ? 'warning' : 'success'} 
            onClick={() => handleResolve(resolveAction)}
            disabled={resolving}
          >
            {resolving ? 'Processing...' : 'Confirm'}
          </Button>
          <Button color="secondary" onClick={() => setResolveModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Disable User Confirmation Modal */}
      <Modal isOpen={disableModal} toggle={() => setDisableModal(false)} size="lg">
        <ModalHeader toggle={() => setDisableModal(false)}>
          Disable User Account
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">
            <strong>Warning:</strong> This action will disable the user's account and prevent them from signing in. 
            The account will be marked as "Removed" and the user will not be able to access the platform.
          </p>
          <div className="mb-3">
            <label htmlFor="disableReason" className="form-label">
              Reason for disabling (optional):
            </label>
            <textarea
              id="disableReason"
              className="form-control"
              rows="3"
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              placeholder="Enter reason for disabling this account..."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="danger" 
            onClick={handleDisableUser}
            disabled={disabling}
          >
            {disabling ? 'Disabling...' : 'Disable Account'}
          </Button>
          <Button color="secondary" onClick={() => {
            setDisableModal(false);
            setDisableReason('');
          }}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReportedUserDetails;
